import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ColumnConfig } from '../types';
import './ColumnSettings.css';

interface ColumnSettingsProps {
  columns: ColumnConfig[];
  onToggleColumn: (columnKey: string) => void;
  onResetColumns: () => void;
  onShowAll: () => void;
  onHideOptional: () => void;
}

export const ColumnSettings: React.FC<ColumnSettingsProps> = ({
  columns,
  onToggleColumn,
  onResetColumns,
  onShowAll,
  onHideOptional
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const visibleCount = columns.filter(col => col.visible).length;
  const totalCount = columns.length;

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      setIsOpen(false);
    }
  };

  const modalContent = (
    <div className="column-settings-modal-overlay" onClick={handleOverlayClick}>
      <div className="column-settings-modal">
        <div className="column-settings-header">
          <h4>Column Visibility Settings</h4>
          <button 
            className="close-button"
            onClick={() => setIsOpen(false)}
            title="Close (Esc)"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="column-settings-content">
          <div className="column-settings-actions">
            <button 
              className="settings-action-btn show-all"
              onClick={onShowAll}
              title="Show all columns"
            >
              Show All
            </button>
            <button 
              className="settings-action-btn hide-optional"
              onClick={onHideOptional}
              title="Hide optional columns"
            >
              Essential Only
            </button>
            <button 
              className="settings-action-btn reset"
              onClick={onResetColumns}
              title="Reset to defaults"
            >
              Reset
            </button>
          </div>

          <div className="column-list">
            {columns.map((column) => (
              <div key={column.key} className="column-item">
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={column.visible}
                    onChange={() => onToggleColumn(column.key)}
                    disabled={column.essential}
                  />
                  <span className="checkmark"></span>
                  <span className={`column-label ${column.essential ? 'essential' : ''}`}>
                    {column.label}
                    {column.essential && <span className="essential-badge">Essential</span>}
                  </span>
                </label>
              </div>
            ))}
          </div>

          <div className="column-settings-footer">
            <p className="settings-note">
              Essential columns cannot be hidden • Press Esc to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="column-settings-trigger control-button"
        onClick={() => setIsOpen(true)}
        title="Column Settings"
      >
        <span className="settings-icon">⚙️</span>
        Columns ({visibleCount}/{totalCount})
      </button>

      {isOpen && createPortal(modalContent, document.body)}
    </>
  );
}; 